import {
  InMemoryWebStorage,
  UserManager,
  UserManagerSettings,
  WebStorageStateStore,
} from 'oidc-client-ts';

// Keycloak realm URL and client id come from the environment (set VITE_KEYCLOAK_AUTHORITY /
// VITE_KEYCLOAK_CLIENT_ID per deployment). Redirect URLs derive from the current origin so they
// automatically match whatever domain serves the app — no per-environment rebuild needed. The
// deployment's Keycloak client must register that origin's /auth/callback and /login URLs.
const authority =
  (import.meta.env.VITE_KEYCLOAK_AUTHORITY as string | undefined) ??
  'http://localhost:8080/realms/klooz-local';
const clientId = (import.meta.env.VITE_KEYCLOAK_CLIENT_ID as string | undefined) ?? 'klooz-creator';
const origin = window.location.origin;

const oidcSettings: UserManagerSettings = {
  authority,
  client_id: clientId,
  redirect_uri: `${origin}/auth/callback`,
  // Hidden-iframe target for prompt=none silent renewals. Served as a standalone page
  // (silent-renew.html) that only calls signinSilentCallback() and posts the result back
  // to the parent frame — without a dedicated URI oidc-client-ts reuses redirect_uri, which
  // boots the whole SPA in the iframe and never notifies the parent, so every iframe renewal
  // times out and drops the session. Registered under the client's localhost:5173/* wildcard.
  silent_redirect_uri: `${origin}/silent-renew.html`,
  // RP-initiated logout returns here after ending the Keycloak SSO session.
  post_logout_redirect_uri: `${origin}/login`,
  response_type: 'code',
  scope: 'openid profile email',
  automaticSilentRenew: true,
  // Keep tokens out of sessionStorage (readable by any injected script). They live in memory;
  // automaticSilentRenew re-establishes the session silently across reloads.
  userStore: new WebStorageStateStore({ store: new InMemoryWebStorage() }),
};

export const userManager = new UserManager(oidcSettings);

// Transient, in-memory logout guard. signoutRedirect() removes the user (flipping the app
// to logged-out) before it navigates the browser to Keycloak's end-session endpoint. During
// that gap the protected layout bounces to /login, whose auto sign-in would otherwise race
// ahead of the logout redirect and silently re-authenticate. Login reads this to skip auto
// sign-in while a logout is mid-flight. Not persisted: a completed logout does a full-page
// redirect, so the next load starts fresh with the guard reset.
let signOutInFlight = false;
export const beginSignOut = (): void => {
  signOutInFlight = true;
};
export const endSignOut = (): void => {
  signOutInFlight = false;
};
export const isSignOutInProgress = (): boolean => signOutInFlight;
